import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { preferencesApi } from '@/core/preferences/export';
import { WidgetHost, WidgetHostContext } from '@/core/widgets/export';
import type { DeepDivePreferences } from '@/core/deep-dive/widgets/deep-dive-rail-widget';
import { registerInlineDeepDiveHandler } from '@/core/deep-dive/routing/deep-dive-window';

export const DEEP_DIVE_PREFERENCES_KEY = 'widget-deep-dive';

export const CONTENT_WIDTH = 1000;

export const DEFAULT_PINNED_APP_IDS = ['bookmarks'];

export type SidePanelId = 'deep-dive';
export const RESERVED_SIDE_PANEL_IDS: SidePanelId[] = ['deep-dive'];

const HASH_PREFIX = 'sp';

interface SidePanelHashState {
  panelId: SidePanelId | null;
  appId: string | null;
  widgetPath: string | null;
  railVisible: boolean;
}

export const RAIL_VISIBLE_MARKER = 'r';
export const RAIL_HIDDEN_MARKER = 'nr';

export function parseSidePanelHash(rawHash: string = window.location.hash): SidePanelHashState {
  const hash = rawHash.replace(/^#/, '');
  const segments = hash.split(':');

  if (segments[0] !== HASH_PREFIX || !segments[1]) {
    return { panelId: null, appId: null, widgetPath: null, railVisible: true };
  }

  let rest = segments.slice(1);
  let railVisible = true;
  if (rest[0] === RAIL_VISIBLE_MARKER || rest[0] === RAIL_HIDDEN_MARKER) {
    railVisible = rest[0] === RAIL_VISIBLE_MARKER;
    rest = rest.slice(1);
  }

  const [encodedIdentifier, encodedPath] = rest;
  if (!encodedIdentifier) {
    return { panelId: null, appId: null, widgetPath: null, railVisible: true };
  }

  let identifier: string;
  let widgetPath: string | null;
  try {
    identifier = decodeURIComponent(encodedIdentifier);
    widgetPath = encodedPath ? decodeURIComponent(encodedPath) : null;
  } catch {
    return { panelId: null, appId: null, widgetPath: null, railVisible: true };
  }

  if (RESERVED_SIDE_PANEL_IDS.includes(identifier as SidePanelId)) {
    return { panelId: identifier as SidePanelId, appId: null, widgetPath, railVisible };
  }
  return { panelId: 'deep-dive', appId: identifier, widgetPath, railVisible };
}

export function sidePanelHash(
  appId: string | null,
  widgetPath: string | null,
  railVisible: boolean,
): string {
  const identifier = appId ?? 'deep-dive';
  const railMarker = `${railVisible ? RAIL_VISIBLE_MARKER : RAIL_HIDDEN_MARKER}:`;
  const pathSegment = widgetPath ? `:${encodeURIComponent(widgetPath)}` : '';
  return `#${HASH_PREFIX}:${railMarker}${encodeURIComponent(identifier)}${pathSegment}`;
}

export function sidePanelShareUrl(
  location: { origin: string; pathname: string; search: string },
  appId: string | null,
  widgetPath: string | null,
  railVisible: boolean,
): string {
  return `${location.origin}${location.pathname}${location.search}${sidePanelHash(appId, widgetPath, railVisible)}`;
}

function writeSidePanelHash(
  panelId: SidePanelId | null,
  appId: string | null,
  widgetPath: string | null,
  railVisible: boolean,
) {
  const currentHash = window.location.hash.replace(/^#/, '');

  if (panelId === null) {
    if (!currentHash.startsWith(`${HASH_PREFIX}:`)) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return;
  }

  const target = sidePanelHash(appId, widgetPath, railVisible);
  window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
}

export interface SidePanelUiState {
  openPanelId: SidePanelId | null;
  selectedAppId: string | null;
  widgetPath: string | null;
  isRailVisible: boolean;
  autoSelectFirstApp: boolean;
  pinnedAppIds: string[];
  openPanel(id: SidePanelId): void;
  closePanel(id: SidePanelId): void;
  togglePanel(id: SidePanelId): void;
  /**
   * @internal Only valid for components already mounted inside
   * SidePanelHostProvider (the rail, side-panel tabs). Callers outside the
   * side panel — e.g. a button in a calendar/article view — must use
   * `useOpenWidget` from `@/core/widget-navigation/widget-navigation-context`
   * instead, which also handles the case where the panel lives in a
   * separate popup window.
   */
  openApp(appId: string, showRail?: boolean): void;
  openRail(): void;
  setPinnedAppIds(ids: string[]): void;
  setWidgetPath(path: string | null): void;
}

export const SidePanelUiContext = createContext<SidePanelUiState>({
  openPanelId: null,
  selectedAppId: null,
  widgetPath: null,
  isRailVisible: true,
  autoSelectFirstApp: true,
  pinnedAppIds: [],
  openPanel() {},
  closePanel() {},
  togglePanel() {},
  openApp() {},
  openRail() {},
  setPinnedAppIds() {},
  setWidgetPath() {},
});

export default function SidePanelHostProvider({
  children,
  autoSelectFirstApp = true,
}: React.PropsWithChildren<{ autoSelectFirstApp?: boolean }>) {
  const [initialHash] = useState(() => parseSidePanelHash());
  const [openPanelId, setOpenPanelId] = useState<SidePanelId | null>(initialHash.panelId);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(initialHash.appId);
  const [widgetPath, setWidgetPathState] = useState<string | null>(initialHash.widgetPath);
  const [isRailVisible, setIsRailVisible] = useState(initialHash.railVisible);
  const [pinnedAppIds, setPinnedAppIdsState] = useState<string[]>([]);

  useEffect(() => {
    preferencesApi
      .getPreferences<DeepDivePreferences>(DEEP_DIVE_PREFERENCES_KEY)
      .then(preferences =>
        setPinnedAppIdsState(preferences?.pinnedAppIds ?? DEFAULT_PINNED_APP_IDS),
      )
      .catch(() => setPinnedAppIdsState(DEFAULT_PINNED_APP_IDS));
  }, []);

  useEffect(() => {
    writeSidePanelHash(openPanelId, selectedAppId, widgetPath, isRailVisible);
  }, [openPanelId, selectedAppId, widgetPath, isRailVisible]);

  // hashchange only fires on a real navigation (address-bar edit/paste, a
  // `#...` link) — never from our own writeSidePanelHash above, which uses
  // history.replaceState. Safe from feedback loops by construction.
  useEffect(() => {
    const handleHashChange = () => {
      const parsed = parseSidePanelHash();
      setOpenPanelId(parsed.panelId);
      setSelectedAppId(parsed.appId);
      setWidgetPathState(parsed.widgetPath);
      setIsRailVisible(parsed.railVisible);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const openPanel = useCallback((id: SidePanelId) => {
    setOpenPanelId(id);
    setWidgetPathState(null);
  }, []);
  const closePanel = useCallback((id: SidePanelId) => {
    setOpenPanelId(prev => (prev === id ? null : prev));
    setWidgetPathState(null);
  }, []);
  const togglePanel = useCallback((id: SidePanelId) => {
    setOpenPanelId(prev => (prev === id ? null : id));
    setWidgetPathState(null);
  }, []);
  const openApp = useCallback((appId: string, showRail = true) => {
    setSelectedAppId(appId);
    setOpenPanelId('deep-dive');
    setWidgetPathState(null);
    setIsRailVisible(showRail);
  }, []);
  const setPinnedAppIds = useCallback(
    (ids: string[]) => {
      const previous = pinnedAppIds;
      setPinnedAppIdsState(ids);
      preferencesApi
        .upsertPreferences(DEEP_DIVE_PREFERENCES_KEY, { pinnedAppIds: ids })
        .catch(() => setPinnedAppIdsState(previous));
    },
    [pinnedAppIds],
  );
  const openRail = useCallback(() => {
    setSelectedAppId(null);
    setOpenPanelId('deep-dive');
    setWidgetPathState(null);
    setIsRailVisible(true);
  }, []);
  const setWidgetPath = useCallback((path: string | null) => {
    setWidgetPathState(path);
  }, []);

  useEffect(() => {
    return registerInlineDeepDiveHandler((appId, path) => {
      openApp(appId, false);
      if (path) setWidgetPath(path);
    });
  }, [openApp, setWidgetPath]);

  const ui: SidePanelUiState = useMemo(
    () => ({
      openPanelId,
      selectedAppId,
      widgetPath,
      isRailVisible,
      autoSelectFirstApp,
      pinnedAppIds,
      openPanel,
      closePanel,
      togglePanel,
      openApp,
      setPinnedAppIds,
      openRail,
      setWidgetPath,
    }),
    [
      openPanelId,
      selectedAppId,
      widgetPath,
      isRailVisible,
      autoSelectFirstApp,
      pinnedAppIds,
      openPanel,
      closePanel,
      togglePanel,
      openApp,
      setPinnedAppIds,
      openRail,
      setWidgetPath,
    ],
  );

  const host: WidgetHost = useMemo(
    () => ({
      get hostId() {
        return 'side-panel-host';
      },
      get activeWidgetIds() {
        return ['bookmarks', 'deep-dive'];
      },
      get currentViewId() {
        return openPanelId ?? '';
      },
      addWidgetById() {
        console.warn('side-panel-host does not support adding widgets');
      },
      removeWidgetById() {
        console.warn('side-panel-host does not support removing widgets');
      },
      setWidgetPath() {
        console.warn('side-panel-host does not support setWidgetPath');
      },
      registerWidgetNavigator() {
        return () => {};
      },
      registerActiveWidgetSelector() {
        return () => {};
      },
      async getWidgetPreferences<T>(id: string): Promise<T> {
        return (await preferencesApi.getPreferences<T>(`widget-${id}`)) || ({} as T);
      },
      async saveWidgetPreferences<T>(id: string, t: T): Promise<T> {
        return preferencesApi.upsertPreferences(`widget-${id}`, t);
      },
    }),
    [openPanelId],
  );

  return (
    <WidgetHostContext.Provider value={host}>
      <SidePanelUiContext.Provider value={ui}>{children}</SidePanelUiContext.Provider>
    </WidgetHostContext.Provider>
  );
}
